import {
  CaseReadFlagsEnum,
  LocationReservationRead,
  ReservationRead,
  ReservationRoomRead,
} from '@/api';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { cloneDeep, debounce } from 'lodash-es';
import { computed, Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from '@/store';

const reservationLoaded = ref(false);
const reservation = ref<Record<string, any>>({
  days: [],
  rooms: [],
  location: {
    name: undefined,
  },
  meeting: {
    name: undefined,
  },
});
const retrievedReservation = ref<Record<string, any>>(cloneDeep(reservation));
export default function useReservationStore(): {
  reservation: Ref<ReservationRead>;
  reservationLoaded: Ref<boolean>;
  reservationChanged: Ref<boolean>;
  reservationLocation: Ref<LocationReservationRead | undefined | null>;
  missingInfo: Ref<boolean>;
  addDay: () => void;
  cloneDay: (index: number) => void;
  cloneRoom: (index: number) => void;
  deleteDay: (index: number) => void;
  setLocale: (locale: string) => void;
  getExistingReservation: () => Promise<any>;
  parseURLParams: () => Promise<any>;
  reservationLoading: Ref<boolean>;
  reservationSubmitting: Ref<boolean>;
  numRooms: Ref<number>;
  postReservation: (method: string) => Promise<any>;
  websiteURL: Ref<string>;
  breakfastSelectable: Ref<boolean>;
  breakfastDefault: Ref<boolean>;
  termsUrl: Ref<string | undefined>;
  earliestDate: Ref<Date | undefined>;
  updateBooking: () => Promise<void>;
  bookingTransitions: Ref<string[]>;
  getBooking: () => Promise<void>;
  bookingId: Ref<number | undefined | null>;
} {
  const store = useStore();
  const debouncedCommit = debounce((val: Record<string, any>) => {
    store.commit('reservation/setReservation', val);
  }, 200);
  watch(
    () => reservation.value,
    (val) => debouncedCommit(val),
    { deep: true },
  );

  const getExistingReservation = async (): Promise<any> => {
    const route = useRoute();
    const id = route.params.id as string;
    const username = route.params.username as string;
    const key = encodeURIComponent(route.query.key as string);

    store.commit('reservation/setConfig', {
      headers: { username: username, key: key },
      withCredentials: false,
    });
    store.commit('reservation/setReservationLoading', true);
    await store.dispatch('reservation/getReservation', { id }).then((res) => {
      reservation.value = cloneDeep(res);
      retrievedReservation.value = cloneDeep(reservation.value);
      reservationLoaded.value = true;
    });
    store.commit('reservation/setReservationLoading', false);
  };

  const route = useRoute();
  const getBooking = async (): Promise<void> => {
    const id = route.params.id as string;
    const username = route.params.username
      ? (route.params.username as string)
      : undefined;
    const key = route.query.key
      ? encodeURIComponent(route.query.key as string)
      : undefined;

    store.commit('reservation/setReservationLoading', true);
    await store
      .dispatch('reservation/getBooking', { id, username, key })
      .then((res) => {
        reservation.value = cloneDeep(res);
        retrievedReservation.value = cloneDeep(reservation.value);
        reservationLoaded.value = true;
      });
    store.commit('reservation/setReservationLoading', false);
  };

  const parseURLParams = async () => {
    const params = route.query;

    if (params.locationID) {
      await store.state.locations.locationsPromise; // wait for locations to be loaded
    }

    reservation.value = await store.dispatch('reservation/parseRouteParams', {
      params,
      locations: store.state.locations.locations,
    });
  };

  const reservationChanged = computed(() => {
    if (!reservationLoaded.value) {
      return false;
    }
    const current = cloneDeep(reservation.value);
    const retrieved = cloneDeep(retrievedReservation.value);
    if (current.location.id !== retrieved.location.id) {
      // Location changed
      return true;
    }
    // Retrieved location does not have all information, 'current' does, even with same ID
    delete current.location;
    delete retrieved.location;

    delete current.locale;
    delete retrieved.locale;

    // Retrieved package deals formats also do not match (and are filled in by the server anyway)
    current.days.map((day: Record<string, any>) => delete day.packageDeals);
    retrieved.days.map((day: Record<string, any>) => delete day.packageDeals);

    return JSON.stringify(current) !== JSON.stringify(retrieved);
  });

  const reservationLoading = computed(
    () => store.state.reservation.reservationLoading,
  );

  const reservationSubmitting = computed(
    () => store.state.reservation.reservationSubmitting,
  );

  const reservationLocation = computed(
    () =>
      reservation.value.location ??
      store.state.reservation.reservation?.location,
  );

  const missingInfo = computed<boolean>(
    () =>
      store.state.reservation.reservation?.flags?.includes(
        CaseReadFlagsEnum.Bricksandbasics,
      ) ?? false,
  );

  const numRooms = computed(() => {
    const blocks = [] as Array<{ startDate: string; endDate: string }>;
    if (!store.state.reservation.reservation?.rooms) {
      return 0;
    }

    store.state.reservation.reservation.rooms.forEach(
      (room: ReservationRoomRead) => {
        const block = blocks.find(
          (block) =>
            block.startDate === room.startDate &&
            block.endDate === room.endDate,
        );
        // Skip invalid room entries with missing or invalid dates
        if (!room.startDate || !room.endDate) {
          return;
        }

        // Ensure dates are valid before comparing
        try {
          new Date(room.startDate);
          new Date(room.endDate);
        } catch (e) {
          return;
        }

        if (!block) {
          blocks.push({
            startDate: room.startDate,
            endDate: room.endDate,
          });
        }
      },
    );
    return blocks.length;
  });

  const websiteURL = computed(() => {
    const website = store.state.reservation.reservation.location?.website;
    if (!website) {
      return '';
    }
    const prefix = website.startsWith('http') ? '' : 'https://';
    return prefix + website;
  });

  const breakfastDefault = computed(
    () =>
      store.state.reservation.reservation.location?.locationSettings
        ?.breakfastEnabledByDefault ?? true,
  );
  const breakfastSelectable = computed(
    () =>
      store.state.reservation.reservation.location?.locationSettings
        ?.breakfastSelectable ?? true,
  );

  const termsUrl = computed(() => {
    const url = store.state.reservation.reservation?.termsUrl;
    return url ? url : undefined;
  });

  const addDay = (): void => {
    reservation.value.days!.push({
      date: undefined,
      halls: [],
      packageDeals: [],
      items: [],
    });
  };

  const findNextDate = (start: Date): { nextDate: Date; newIndex: number } => {
    let nextPossibleDay = addDays(start, 1);
    let newIndex = -1;
    let matchingIndex = reservation.value.days.findIndex((day: any) => {
      if (!day.date) {
        return false;
      }
      return isSameDay(parseISO(day.date), nextPossibleDay);
    });

    while (matchingIndex !== -1) {
      nextPossibleDay = addDays(nextPossibleDay, 1);
      newIndex = matchingIndex; // Place new day after closest earlier date.
      matchingIndex = reservation.value.days.findIndex((day: any) =>
        isSameDay(parseISO(day.date), nextPossibleDay),
      );
    }
    return { nextDate: nextPossibleDay, newIndex };
  };

  const cloneDay = (index: number): void => {
    const curDay = reservation.value.days[index];
    const newDay = cloneDeep(curDay);
    if (newDay.date) {
      const { nextDate, newIndex } = findNextDate(parseISO(newDay.date));
      newDay.date = format(nextDate, 'yyyy-MM-dd');
      if (newIndex !== -1) {
        index = newIndex;
      }
    }
    reservation.value.days!.splice(index + 1, 0, newDay);
  };

  const cloneRoom = (index: number): void => {
    const curDay = reservation.value.rooms[index];
    const newDay = cloneDeep(curDay);
    reservation.value.rooms!.splice(index + 1, 0, newDay);
  };

  const deleteDay = (index: number) => {
    reservation.value.days!.splice(index, 1);
  };

  const setLocale = (locale: string) => {
    store.commit('reservation/setReservationLocale', locale);
  };

  const postReservation = async (status: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'submit_booking' });
    }
    store.commit('reservation/setReservationSubmitting', true);
    await store.dispatch('reservation/setBookingStatus', status);
    store.commit('reservation/setReservationSubmitting', false);
  };

  const bookingId = computed(() => store.state.reservation.bookingId);

  const postBooking = async () => {
    await store.dispatch('reservation/postBooking');
  };

  const putBooking = async () => {
    store.commit('reservation/setReservationSubmitting', true);
    await store.dispatch('reservation/putBooking');
    store.commit('reservation/setReservationSubmitting', false);
  };

  const updateBooking = async () => {
    if (!reservation.value.emailaddress) {
      return;
    }
    if (bookingId.value === undefined || bookingId.value === null) {
      await postBooking();
    } else {
      await putBooking();
    }
  };

  const earliestDate = computed<Date | undefined>(() => {
    const reservation = store.state.reservation.reservation;
    return (
      (reservation.rooms ?? []) // get dates from rooms
        .filter((room) => room.startDate)
        .map((room) => parseISO(room.startDate!))
        // combine with MICE days
        .concat(
          // get dates from days
          ...(reservation.days ?? [])
            .filter((day) => day.date!)
            .map((day) => parseISO(day.date!)),
        )
        // reduce to earliest
        .reduce((a: undefined | Date, b) => {
          if (a && b) {
            return a < b ? a : b;
          }
          return a ?? b;
        }, undefined)
    );
  });

  const bookingTransitions = computed(
    () => store.state.reservation.transitions,
  );

  return {
    numRooms,
    reservation,
    reservationLoaded,
    reservationChanged,
    addDay,
    deleteDay,
    setLocale,
    getExistingReservation,
    postReservation,
    reservationLoading,
    reservationSubmitting,
    parseURLParams,
    websiteURL,
    cloneDay,
    cloneRoom,
    missingInfo,
    breakfastSelectable,
    breakfastDefault,
    termsUrl,
    earliestDate,
    reservationLocation,
    updateBooking,
    bookingTransitions,
    getBooking,
    bookingId,
  };
}
